// src/App.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LoadingProvider } from "./context/LoadingContext";
import { AdminAuthProvider } from "./context/AdminAuthContext"; // Import Admin Provider
import { useTheme } from "./config/theme.config";

// Import Guards
import {
  ProtectedRoute,
  PublicRoute,
  VerifyRoute,
} from "./components/app/auth/ProtectedRoute";
import {
  AdminProtectedRoute,
  PublicAdminRoute,
  AdminRedirect,
} from "./components/app/auth/AdminProtectedRoute"; // Import Admin Guards

// Importar páginas
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import Dashboard from "./pages/customer/Dashboard";
import VerifyPage from "./pages/customer/VerifyPage";
import AdminLoginPage from "./pages/auth/AdminLoginPage"; // Import Admin Login Page
import AdminDashboard from "./pages/admin/AdminDashboard"; // Import Admin Dashboard
import AdminUsersPage from "./pages/admin/AdminUsersPage"; // Import Admin Users Page
import AdminCustomersPage from "./pages/admin/AdminCustomersPage"; // Import Admin Customers Page
import AdminRolesPage from "./pages/admin/AdminRolesPage"; // Import Admin Roles Page
import AdminCarrierServicesPage from "./pages/admin/AdminCarrierServicesPage"; // Import Admin Carrier Services Page
import AdminOrdersPage from "./pages/admin/AdminOrdersPage"; // Import Admin Orders Page
import AdminOrderDetailPage from "./pages/admin/AdminOrderDetailPage"; // Import Admin Order Detail Page
import AdminRatesReportPage from "./pages/admin/AdminRatesReportPage"; // Import Admin Rates Report Page
import AdminDispatchersPage from "./pages/admin/AdminDispatchersPage"; // Import Admin Dispatchers Page
import AdminDispatcherDetailPage from "./pages/admin/AdminDispatcherDetailPage"; // Import Admin Dispatcher Detail Page
import AdminZipcodeDispatchZonesPage from "./pages/admin/AdminZipcodeDispatchZonesPage"; // Import Admin Zipcode Dispatch Zones Page
import AdminZipcodeDispatchZoneDetailPage from "./pages/admin/AdminZipcodeDispatchZoneDetailPage"; // Import Admin Zipcode Dispatch Zone Detail Page
import ProfilePage from "./pages/customer/ProfilePage"; // Import Profile Page
import SendPackagePage from "./pages/customer/SendPackagePageV3";
import OrderDetailPage from "./pages/customer/OrderDetailPage"; // Import OrderDetailPage

// Importar estilos
import "./App.css";
import AddressPage from "./pages/customer/AddressPage";
import MyOrdersPage from "./pages/customer/MyOrdersPage";
import MyPaymentsPage from "./pages/customer/MyPaymentsPage";
import CalculatorPage from "./pages/CalculatorPage";
import InstructivePage from "./pages/InstructivePage";
import TrackPackagePage from "./pages/TrackPackagePage";
import FAQPage from "./pages/FAQPage";
import ProhibitedItemsPage from "./pages/ProhibitedItemsPage";
import ContactPage from "./pages/ContactPage";
import PickUpPage from "./pages/PickUpPage";
import AboutUsPage from "./pages/AboutUsPage";
import ServicesPage from "./pages/ServicesPage";
import CompanyPage from "./pages/CompanyPage";

import ScrollToHash from "./components/utility/ScrollToHash";
import EnvironmentBanner from "./components/common/EnvironmentBanner";

function App() {
  const theme = useTheme();

  // Aplicar el favicon del tema al cargar la aplicación
  useEffect(() => {
    const favicon = document.querySelector(
      "link[rel*='icon']"
    ) as HTMLLinkElement;
    if (favicon) {
      favicon.href = theme.brand.favicon;
    }
  }, [theme]);

  return (
    <AuthProvider>
      <AdminAuthProvider>
        {" "}
        {/* Wrap with AdminAuthProvider */}
        <LoadingProvider>
          <Router>
            <ScrollToHash />
            <div className="App">
              <EnvironmentBanner />
              <Routes>
                {/* === Public Routes === */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/calculator" element={<CalculatorPage />} />
                <Route path="/instructive" element={<InstructivePage />} />
                <Route path="/track" element={<TrackPackagePage />} />
                <Route path="/preguntas-frecuentes" element={<FAQPage />} />
                <Route path="/articulos-prohibidos" element={<ProhibitedItemsPage />} />
                <Route path="/contacto" element={<ContactPage />} />
                <Route path="/pickup" element={<PickUpPage />} />
                <Route path="/about-us" element={<AboutUsPage />} />
                <Route path="/servicios" element={<ServicesPage />} />
                <Route path="/company" element={<CompanyPage />} />

                {/* === Customer Auth Routes === */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPasswordPage />
                    </PublicRoute>
                  }
                />

                {/* === Admin Redirect Route === */}
                <Route path="/admin" element={<AdminRedirect />} />

                {/* === Admin Auth Route === */}
                <Route
                  path="/admin/login" // Changed from /admin to /admin/login
                  element={
                    <PublicAdminRoute>
                      <AdminLoginPage />
                    </PublicAdminRoute>
                  }
                />

                {/* === Customer Protected Routes === */}
                <Route
                  path="/customer/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customer/verify/:token"
                  element={
                    <VerifyRoute>
                      <VerifyPage />
                    </VerifyRoute>
                  }
                />

                {/* === Customer Profile Route === */}
                <Route
                  path="/customer/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/customer/address"
                  element={
                    <ProtectedRoute>
                      <AddressPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/customer/send-package"
                  element={
                    <ProtectedRoute>
                      <SendPackagePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/customer/order/:idOrder"
                  element={
                    <ProtectedRoute>
                      <OrderDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/customer/my-orders"
                  element={
                    <ProtectedRoute>
                      <MyOrdersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/customer/my-payments"
                  element={
                    <ProtectedRoute>
                      <MyPaymentsPage />
                    </ProtectedRoute>
                  }
                />

                {/* === Admin Protected Routes === */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminProtectedRoute>
                      <AdminUsersPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <AdminProtectedRoute>
                      <AdminCustomersPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/roles"
                  element={
                    <AdminProtectedRoute>
                      <AdminRolesPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/orders"
                  element={
                    <AdminProtectedRoute>
                      <AdminOrdersPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/order/:id"
                  element={
                    <AdminProtectedRoute>
                      <AdminOrderDetailPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/courier-services"
                  element={
                    <AdminProtectedRoute>
                      <AdminCarrierServicesPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/rates-report"
                  element={
                    <AdminProtectedRoute>
                      <AdminRatesReportPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dispatchers"
                  element={
                    <AdminProtectedRoute>
                      <AdminDispatchersPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dispatcher/:id"
                  element={
                    <AdminProtectedRoute>
                      <AdminDispatcherDetailPage />
                    </AdminProtectedRoute>
                  }
                />

                <Route
                  path="/admin/zipcode-dispatch-zones"
                  element={
                    <AdminProtectedRoute>
                      <AdminZipcodeDispatchZonesPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/zipcode-dispatch-zone/:id"
                  element={
                    <AdminProtectedRoute>
                      <AdminZipcodeDispatchZoneDetailPage />
                    </AdminProtectedRoute>
                  }
                />

                {/* Fallback Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </LoadingProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
